import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { PageHeader } from '../components/PageHeader';
import { PageLayout } from '../components/PageLayout';
import { Portal } from '../components/Portal';
import { authenticatedFetch } from '../utils/fetchHelper';
import toast from 'react-hot-toast';

interface WarmingSession {
  id: string;
  phoneNumber: string;
  status: 'warming' | 'completed' | 'paused' | 'failed';
  currentDay: number;
  healthScore: number;
  startedAt: string;
  completedAt?: string;
  metrics: {
    messagesSent: number;
    messagesDelivered: number;
    messagesRead: number;
    messagesReceived: number;
    deliveryRate: number;
    readRate: number;
    responseRate: number;
    companiesContacted: number;
  };
}

interface Connection {
  id: string;
  phoneNumber: string;
  provider: string;
  status: string;
  hasActiveWarming?: boolean;
}

export function WarmingPage() {
  const [sessions, setSessions] = useState<WarmingSession[]>([]);
  const [availableConnections, setAvailableConnections] = useState<Connection[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingConnections, setLoadingConnections] = useState(false);
  const [startingWarming, setStartingWarming] = useState<string | null>(null);
  const [showStartModal, setShowStartModal] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState<Connection | null>(null);
  const [autoProfile, setAutoProfile] = useState(false);
  const [stats, setStats] = useState({
    warming: 0,
    completed: 0,
    avgScore: 0,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    await Promise.all([loadSessions(), loadConnections()]);
  };

  const loadSessions = async () => {
    setLoading(true);
    try {
      const res = await authenticatedFetch('/api/warming');
      if (res.ok) {
        const json = await res.json();
        setSessions(json.sessions || []);

        // Calcular estatísticas
        const warming = json.sessions.filter((s: WarmingSession) => s.status === 'warming').length;
        const completed = json.sessions.filter((s: WarmingSession) => s.status === 'completed').length;
        const avgScore = json.sessions.length > 0
          ? Math.round(json.sessions.reduce((acc: number, s: WarmingSession) => acc + s.healthScore, 0) / json.sessions.length)
          : 0;

        setStats({ warming, completed, avgScore });
      } else {
        toast.error('Erro ao carregar sessões de aquecimento');
      }
    } catch (e) {
      console.error(e);
      toast.error('Erro ao carregar sessões de aquecimento');
    } finally {
      setLoading(false);
    }
  };

  const loadConnections = async () => {
    setLoadingConnections(true);
    try {
      const res = await authenticatedFetch('/api/connections');
      if (res.ok) {
        const json = await res.json();
        const connections = json.connections || [];

        // Filtrar apenas conexões ativas e que NÃO estão em aquecimento
        const activeSessions = sessions.filter(s => s.status === 'warming' || s.status === 'paused');
        const activeWarmingPhones = activeSessions.map(s => s.phoneNumber);

        const available = connections.filter((c: Connection) =>
          c.status === 'ACTIVE' && !activeWarmingPhones.includes(c.phoneNumber)
        );

        setAvailableConnections(available);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingConnections(false);
    }
  };

  const handleStartWarming = async () => {
    if (!selectedConnection) return;

    setStartingWarming(selectedConnection.id);
    try {
      const res = await authenticatedFetch('/api/warming', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          connectionId: selectedConnection.id,
          config: {
            auto_profile: autoProfile,
            pause_hours: [0, 6],
            enable_network: true,
          },
        }),
      });

      if (res.ok) {
        toast.success('Aquecimento iniciado com sucesso!');
        setShowStartModal(false);
        setSelectedConnection(null);
        setAutoProfile(false);
        await loadData();
      } else {
        const json = await res.json();
        toast.error(json.message || 'Erro ao iniciar aquecimento');
      }
    } catch (e) {
      console.error(e);
      toast.error('Erro ao iniciar aquecimento');
    } finally {
      setStartingWarming(null);
    }
  };

  const getHealthColor = (score: number) => {
    if (score >= 80) return 'text-green-600 dark:text-green-400';
    if (score >= 60) return 'text-yellow-600 dark:text-yellow-400';
    if (score >= 40) return 'text-orange-600 dark:text-orange-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getHealthLabel = (score: number) => {
    if (score >= 80) return 'Saudável';
    if (score >= 60) return 'Normal';
    if (score >= 40) return 'Atenção';
    return 'Risco';
  };

  const getHealthIcon = (score: number) => {
    if (score >= 80) return '🟢';
    if (score >= 60) return '🟡';
    if (score >= 40) return '🟠';
    return '🔴';
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      warming: 'Aquecendo',
      completed: 'Concluído',
      paused: 'Pausado',
      failed: 'Falhou',
    };
    return labels[status] || status;
  };

  const getStatusColor = (status: string) => {
    if (status === 'completed') return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
    if (status === 'warming') return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
    if (status === 'paused') return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
    return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
  };

  const formatDate = (s: string) => {
    try {
      return new Date(s).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return s;
    }
  };

  return (
    <>
      <Header title="Aquecimento" subtitle="Prepare seus chips para envios em larga escala" />
      <PageLayout>
        <PageHeader
          title="Aquecimento"
          breadcrumbs={[
            { label: 'Home', href: '/dashboard' },
            { label: 'Aquecimento' },
          ]}
        />

        <div className="space-y-8 w-full">
          {/* Cards de estatísticas */}
          <div className="mb-6 rounded-2xl border border-gray-200 bg-white p-4 sm:p-6 dark:border-gray-800 dark:bg-white/[0.03]">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="font-semibold text-gray-800 dark:text-white/90">Visão Geral</h2>
              <button
                type="button"
                onClick={async () => {
                  await loadData();
                  setSelectedConnection(null);
                  setShowStartModal(true);
                }}
                disabled={loading}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-transparent text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <i className="fas fa-plus text-base shrink-0" aria-hidden />
                Aquecer um novo chip
              </button>
            </div>

            <div className="grid grid-cols-1 rounded-xl border border-gray-200 sm:grid-cols-2 lg:grid-cols-3 lg:divide-x lg:divide-y-0 dark:divide-gray-800 dark:border-gray-800">
              <div className="border-b p-5 sm:border-r lg:border-b-0">
                <div className="mb-1.5 flex items-center gap-2">
                  <span className="text-blue-600 dark:text-blue-400 text-xl" aria-hidden>
                    🔥
                  </span>
                  <p className="text-sm text-gray-400 dark:text-gray-500">Em aquecimento</p>
                </div>
                <h3 className="text-3xl text-gray-800 dark:text-white/90">
                  {loading ? '...' : stats.warming}
                </h3>
              </div>

              <div className="border-b p-5 lg:border-b-0">
                <div className="mb-1.5 flex items-center gap-2">
                  <span className="text-green-600 dark:text-green-400 text-xl" aria-hidden>
                    ✅
                  </span>
                  <p className="text-sm text-gray-400 dark:text-gray-500">Concluídos</p>
                </div>
                <h3 className="text-3xl text-gray-800 dark:text-white/90">
                  {loading ? '...' : stats.completed}
                </h3>
              </div>

              <div className="p-5">
                <div className="mb-1.5 flex items-center gap-2">
                  <span className="text-teal-600 dark:text-teal-400 text-xl" aria-hidden>
                    📊
                  </span>
                  <p className="text-sm text-gray-400 dark:text-gray-500">Score médio</p>
                </div>
                <h3 className="text-3xl text-gray-800 dark:text-white/90">
                  {loading ? '...' : `${stats.avgScore}/100`}
                </h3>
              </div>
            </div>
          </div>

          {/* Tabela de chips em aquecimento */}
          <div className="card-padrao overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="font-semibold text-gray-800 dark:text-white/90">Chips em Aquecimento</h2>
            </div>

            <div className="overflow-x-auto">
              {loading ? (
                <div className="p-6 animate-pulse space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-16 bg-gray-100 dark:bg-gray-800 rounded" />
                  ))}
                </div>
              ) : sessions.length === 0 ? (
                <div className="p-12 text-center">
                  <div className="text-6xl mb-4">🔥</div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    Nenhum chip em aquecimento
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 mb-6">
                    {availableConnections.length > 0
                      ? 'Clique em "Aquecer um novo chip" para escolher um número e iniciar.'
                      : 'Conecte um número em WhatsApp primeiro. Depois clique em "Aquecer um novo chip".'}
                  </p>
                  <button
                    type="button"
                    onClick={async () => {
                      await loadData();
                      setSelectedConnection(null);
                      setShowStartModal(true);
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-transparent text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700"
                  >
                    <i className="fas fa-plus text-base" aria-hidden />
                    Aquecer um novo chip
                  </button>
                  {availableConnections.length === 0 && (
                    <Link
                      to="/whatsapp"
                      className="ml-3 inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm font-medium"
                    >
                      Ir para Conexões
                    </Link>
                  )}
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800/50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Número
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Dia
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Score
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Empresas
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Taxa Entrega
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Início
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Ação
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-white/[0.02] divide-y divide-gray-200 dark:divide-gray-700">
                    {sessions.map((session) => (
                      <tr key={session.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/30">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white/90">
                          {session.phoneNumber}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(session.status)}`}>
                            {getStatusLabel(session.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                          {session.currentDay}/14
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{getHealthIcon(session.healthScore)}</span>
                            <div>
                              <div className={`text-sm font-medium ${getHealthColor(session.healthScore)}`}>
                                {session.healthScore}/100
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {getHealthLabel(session.healthScore)}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                          {session.metrics?.companiesContacted || 0}/58
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                          {session.metrics?.deliveryRate || 0}%
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                          {formatDate(session.startedAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                          <Link
                            to={`/aquecimento/${session.id}`}
                            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                          >
                            Ver Detalhes
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </PageLayout>

      {/* Modal: escolher chip e confirmar (não altera bot / campanhas / fluxo interativo) */}
      {showStartModal && (
        <Portal>
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" onClick={() => !selectedConnection && setShowStartModal(false)}>
            <div className="bg-white dark:bg-gray-800 rounded-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col shadow-xl" onClick={(e) => e.stopPropagation()}>
              {!selectedConnection ? (
                <>
                  <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Escolha o chip para aquecer
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Selecione um número conectado. Campanhas e bot continuam funcionando normalmente.
                    </p>
                  </div>
                  <div className="p-4 overflow-y-auto flex-1">
                    {availableConnections.length === 0 ? (
                      <div className="text-center py-8">
                        <p className="text-gray-500 dark:text-gray-400 mb-4">
                          Nenhum número disponível. Conecte um número em WhatsApp ou aguarde o término de outro aquecimento.
                        </p>
                        <Link
                          to="/whatsapp"
                          className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium"
                        >
                          Ir para Conexões
                        </Link>
                      </div>
                    ) : (
                      <ul className="space-y-2">
                        {availableConnections.map((conn) => (
                          <li key={conn.id}>
                            <button
                              type="button"
                              onClick={() => setSelectedConnection(conn)}
                              className="w-full flex items-center justify-between gap-3 p-4 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-emerald-500 dark:hover:border-emerald-500 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left"
                            >
                              <div className="flex items-center gap-3">
                                <span className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                                  <i className="fas fa-phone text-lg" aria-hidden />
                                </span>
                                <div>
                                  <p className="font-medium text-gray-900 dark:text-white">{conn.phoneNumber}</p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">{conn.provider}</p>
                                </div>
                              </div>
                              <span className="px-2 py-1 text-xs bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 rounded-full shrink-0">
                                Ativo
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                      type="button"
                      onClick={() => setShowStartModal(false)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      Fechar
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Iniciar aquecimento
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Este chip será usado apenas para aquecimento. Bot e campanhas não são alterados.
                    </p>
                  </div>
                  <div className="p-6 overflow-y-auto flex-1">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      Número selecionado:
                    </p>
                    <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-4 mb-4">
                      <p className="font-medium text-gray-900 dark:text-white">{selectedConnection.phoneNumber}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{selectedConnection.provider}</p>
                    </div>

                    <div className="space-y-3 mb-4">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={autoProfile}
                          onChange={(e) => setAutoProfile(e.target.checked)}
                          className="mt-1"
                        />
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            Configurar perfil automaticamente
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Nome, foto e status aleatórios para o chip
                          </p>
                        </div>
                      </label>
                    </div>

                    <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                      <p className="text-xs text-amber-800 dark:text-amber-200">
                        ⏱️ O aquecimento dura 14 dias e roda automaticamente. Suas campanhas e fluxos continuam normais.
                      </p>
                    </div>
                  </div>

                  <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex gap-3">
                    <button
                      type="button"
                      onClick={() => setSelectedConnection(null)}
                      disabled={startingWarming !== null}
                      className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      Voltar
                    </button>
                    <button
                      onClick={() => {
                        setShowStartModal(false);
                        setSelectedConnection(null);
                        setAutoProfile(false);
                      }}
                      disabled={startingWarming !== null}
                      className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleStartWarming}
                      disabled={startingWarming !== null}
                      className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                    >
                      {startingWarming ? 'Iniciando...' : 'Iniciar aquecimento'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </Portal>
      )}
    </>
  );
}
